import React from "react";
import { calType, locationFacility } from "./testConstant";
import { AutoCompletes, TextFields } from "../../utils/commonComponent/Custom/custominput";
import { DeleteButton, ModalButton } from "../../utils/commonComponent/Custom/Button";
import TablePagination from "@mui/material/TablePagination";
import IconButton from "@mui/material/IconButton";
import FirstPageIcon from "@mui/icons-material/FirstPage";
import LastPageIcon from "@mui/icons-material/LastPage";
import KeyboardArrowLeft from "@mui/icons-material/KeyboardArrowLeft";
import KeyboardArrowRight from "@mui/icons-material/KeyboardArrowRight";

function TablePaginationActions(props) {
  const { count, page, rowsPerPage, onPageChange } = props;

  const handleFirstPage = (event) => {
    onPageChange(event, 0);
  };

  const handleBack = (event) => {
    onPageChange(event, page - 1);
  };

  const handleNext = (event) => {
    onPageChange(event, page + 1);
  };

  const handleLastPage = (event) => {
    onPageChange(event, Math.max(0, Math.ceil(count / rowsPerPage) - 1));
  };

  return (
    <div className="flex items-center">
      <IconButton
        onClick={handleFirstPage}
        disabled={page === 0}
        aria-label="first page"
      >
        <FirstPageIcon />
      </IconButton>
      <IconButton
        onClick={handleBack}
        disabled={page === 0}
        aria-label="previous page"
      >
        <KeyboardArrowLeft />
      </IconButton>
      <IconButton
        onClick={handleNext}
        disabled={page >= Math.ceil(count / rowsPerPage) - 1}
        aria-label="next page"
      >
        <KeyboardArrowRight />
      </IconButton>
      <IconButton
        onClick={handleLastPage}
        disabled={page >= Math.ceil(count / rowsPerPage) - 1}
        aria-label="last page"
      >
        <LastPageIcon />
      </IconButton>
    </div>
  );
}


// Helper functions for value#unit parsing
const parseValueUnit = (data) => {
  if (!data) return { value: "", unit: "" };
  const [value, unit] = data.split("#");
  return { value: value || "", unit: unit || "" };
};

const formatValueUnit = (value, unit) =>
  unit ? `${value || ""}#${unit}` : value || "";

// Modal component for Test Range Configuration
const RangeModal = React.memo(({ mt, updateMaterialTestingField, unitList, equipmentList, inputStates }) => {
  const [localValues, setLocalValues] = React.useState({
    rangeLower: parseValueUnit(mt.rangeLower),
    rangeHigher: parseValueUnit(mt.rangeHigher),
    muValue: parseValueUnit(mt.muValue),
    testValue: parseValueUnit(mt.testValue),
    location: mt.location || null,
    masterEquipmentId: mt.masterEquipmentId || [], // store selected equipment IDs
    showStdEquipment: true, // false = all equipment
  });

  React.useEffect(() => {
    setLocalValues({
      rangeLower: parseValueUnit(mt.rangeLower),
      rangeHigher: parseValueUnit(mt.rangeHigher),
      muValue: parseValueUnit(mt.muValue),
      testValue: parseValueUnit(mt.testValue),
      location: mt.location || null,
      masterEquipmentId: mt.masterEquipmentId || [],
      showStdEquipment: true,
    });
  }, [mt]);

  const handleChange = (fieldKey, value, unit) => {
    setLocalValues((prev) => ({
      ...prev,
      [fieldKey]: { value, unit },
    }));

    updateMaterialTestingField(mt.id, fieldKey, formatValueUnit(value, unit));
  };

  const handleLocationChange = (selected) => {
    console.log({selected})
    setLocalValues((prev) => ({
      ...prev,
      location: selected,
    }));
    updateMaterialTestingField(mt.id, "location", selected);
  };

  const handleEquipmentChange = (selectedIds) => {
    setLocalValues((prev) => ({
      ...prev,
      masterEquipmentId: selectedIds,
    }));
    updateMaterialTestingField(mt.id, "masterEquipmentId", selectedIds);
  };

  const handleEquipmentTypeChange = () => {
    setLocalValues((prev) => ({
      ...prev,
      showStdEquipment: !prev.showStdEquipment,
      // Don't reset selected equipment - keep the stored values
    }));
  };
  // Filter equipment based on type selection
  const getFilteredEquipmentList = () => {
    if (!equipmentList || equipmentList.length === 0) return [];    
    if (localValues.showStdEquipment) {
      // Show all equipment by default
      return equipmentList;
    } else {
      // Show STD equipment filtered by groupId and disciplineId from inputStates
      return equipmentList.filter(equipment => 
        equipment.groupId === inputStates?.groupId && 
        equipment.disciplineId === inputStates?.discplineId
      );
    }
  };

  const renderFieldWithUnit = (label, fieldKey) => {
    const { value, unit } = localValues[fieldKey];

    return (
      <div className="mb-4">
        <label className="block text-sm font-medium mb-2">{label}</label>
        <div className="flex gap-3">
          <div className="flex-1">
            <TextFields
              label=""
              value={value}
              onChange={(e) => handleChange(fieldKey, e.target.value, unit)}
              placeholder={`Enter ${label.toLowerCase()}`}
            />
          </div>
          <div className="w-36">
            <select
              value={unit}
              onChange={(e) => handleChange(fieldKey, value, e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 h-10"
            >
              <option value="">Select Unit</option>
              {unitList?.map((unit) => (
                <option key={unit.id} value={unit.symbol}>
                  {unit.symbol}
                </option>
              ))}
            </select>
          </div>
        </div>
      </div>
    );
  };

  const filteredEquipmentList = getFilteredEquipmentList();
  return (
    <div className="space-y-2 p-4 min-w-[500px]">
      {renderFieldWithUnit("Range Lower Limit", "rangeLower")}
      {renderFieldWithUnit("Range Higher Limit", "rangeHigher")}
      {renderFieldWithUnit("MU Value", "muValue")}
      {renderFieldWithUnit("Test Value", "testValue")}

      {/* Location Facility Single Select */}
      <div className="mb-4">
        <label className="block text-sm font-medium mb-2">Location</label>
        <AutoCompletes
          label="Select Location"
          placeholder="Search Location..."
          options={locationFacility}
          value={localValues.location}
          onChange={handleLocationChange}
          labelKey="label"
          valueKey="id"
          fullWidth
        />
      </div>

      {/* Equipment Selection */}
      <div className="mb-4">
        <div className="flex items-center justify-between mb-2">
          <label className="block text-sm font-medium mb-2">Select Equipment</label>
          <button
            type="button"
            onClick={handleEquipmentTypeChange}
            className="px-3 py-1 text-sm rounded-md border bg-blue-500 text-white border-blue-500 hover:bg-blue-600"
          >
            {localValues.showStdEquipment ? "Equipment (All)" : "Equipment(By Discipline)"}
          </button>
        </div>        
        <AutoCompletes
          label="Select Equipment"
          placeholder="Search equipment..."
          options={filteredEquipmentList}
          value={localValues.masterEquipmentId}
          onChange={handleEquipmentChange}
          multiple
          labelKey="equipmentName"
          valueKey="id"
          fullWidth
        />
      </div>
    </div>
  );
});

const CERTIFICATE_TYPE_OPTIONS = [
  { id: 1, name: "Separate" },
  { id: 2, name: "Combine" }
];

const MaterialTestingTable = ({
  materialTestings,
  deleteMaterialTesting,
  updateMaterialTestingField,
  unitList,
  equipmentList,
  inputStates,
  permissions,
  page,
  pageSize,
  totalRecords,
  onPageChange,
  onPageSizeChange,
}) => {
  return (
    <div>
      {materialTestings?.length === 0 ? (
        <div className="text-gray-500 text-center py-4">
          No material testings added yet
        </div>
      ) : (
        <>
        <table className="w-full table-auto border border-gray-300 rounded-md">
          <thead className="bg-gray-100 text-sm">
            <tr>
              <th className="border px-4 py-2 text-left w-16">Sr.No.</th>
              <th className="border px-4 py-2 text-left">Scope Type</th>
              <th className="border px-4 py-2 text-left">Test Name</th>
              <th className="border px-4 py-2 text-left">Test Method</th>
              <th className="border px-4 py-2 text-left">Type</th>
              <th className="border px-4 py-2 text-center w-32">Actions</th>
            </tr>
          </thead>
          <tbody className="text-sm">
            {materialTestings?.map((mt, index) => (
              <tr key={mt.id} className="border-b">
                  <td className="border px-4 py-2">
                    {page * pageSize + (index + 1)}
                  </td>
                <td className="border px-4 py-2">
                  <AutoCompletes
                    label="Scope Type"
                    options={calType}
                    value={mt.scopeType}
                    onChange={(selectedId) =>
                      updateMaterialTestingField(mt.id, "scopeType", selectedId)
                    }
                    labelKey="label"
                    valueKey="id"
                    fullWidth
                  />
                </td>
                <td className="border px-4 py-2">
                  <TextFields
                    label=""
                    value={mt.testName || ""}
                    onChange={(e) =>
                      updateMaterialTestingField(mt.id, "testName", e.target.value)
                    }
                  />
                </td>
                <td className="border px-4 py-2">
                  <TextFields
                    label=""
                    value={mt.testMethod || ""}
                    onChange={(e) =>
                      updateMaterialTestingField(mt.id, "testMethod", e.target.value)
                    }
                  />
                </td>
                <td className="border px-4 py-2">
                  <AutoCompletes
                    label="Type"
                    options={CERTIFICATE_TYPE_OPTIONS}
                    value={mt.observationType}
                    onChange={(selectedId) =>
                      updateMaterialTestingField(mt.id, "observationType", selectedId)
                    }
                    labelKey="name"
                    valueKey="id"
                    fullWidth
                  />
                </td>
                <td className="border px-4 py-2 text-center flex items-center gap-2">
                  <ModalButton buttonLabel="View" modalTitle="Add Range">
                    <RangeModal
                      mt={mt}
                      updateMaterialTestingField={updateMaterialTestingField}
                      unitList={unitList}
                      equipmentList={equipmentList}
                      inputStates={inputStates}
                    />
                  </ModalButton>
                  {(permissions.delete || permissions.allAccess) && (
                  <DeleteButton
                    outline
                    onClick={() => {
                      if (
                        window.confirm(
                          "Are you sure you want to delete this material testing?"
                        )
                      ) {
                        deleteMaterialTesting(mt.id, !mt.isNew);
                      }
                    }}
                  />
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>

          {/*TablePagination */}
          <TablePagination
            component="div"
            count={totalRecords}
            page={page}
            onPageChange={onPageChange}
            rowsPerPage={pageSize}
            onRowsPerPageChange={onPageSizeChange}
            rowsPerPageOptions={[25, 50, 100]}
            ActionsComponent={TablePaginationActions}
          />
        </>
      )}
    </div>
  );
};

export default React.memo(MaterialTestingTable);